import { useEffect, useState } from 'react';

import {
  DEFAULT_CONDITIONS,
  DEFAULT_FREE_SHIPPING_REASONS,
  DEFAULT_GIFTCARD_REASONS,
  DEFAULT_ORDER_STATES,
  DEFAULT_REFUND_REASONS,
  DEFAULT_RETURN_REASONS,
  DEFAULT_YPACKAGE_STATES,
} from './constants';
import { fetchOrderConstants } from './fetchOrders';
import type { OrderConstants } from './types';

const FALLBACK: OrderConstants = {
  states: DEFAULT_ORDER_STATES,
  ypackageStates: DEFAULT_YPACKAGE_STATES,
  conditions: DEFAULT_CONDITIONS,
  refundReasons: DEFAULT_REFUND_REASONS,
  giftcardReasons: DEFAULT_GIFTCARD_REASONS,
  returnReasons: DEFAULT_RETURN_REASONS,
  freeShippingReasons: DEFAULT_FREE_SHIPPING_REASONS,
};

/**
 * Every list must be non-empty. A partial payload is worse than a failed one: it would be cached
 * as success, so the dropdowns it covers render empty with no fallback banner, chips fall back to
 * raw codes, and the cache blocks any retry for the session. Worse still, `toChoices(undefined)`
 * throws during render — one missing list white-screens the whole page.
 */
const usableConstants = (result: OrderConstants | null | undefined): boolean =>
  Boolean(
    result &&
      [
        result.states,
        result.ypackageStates,
        result.conditions,
        result.refundReasons,
        result.giftcardReasons,
        result.returnReasons,
        result.freeShippingReasons,
      ].every(list => Array.isArray(list) && list.length > 0)
  );

let cache: OrderConstants | null = null;

/**
 * Test seam. The cache has no other invalidation point for the lifetime of the process, so
 * without this each test inherits whatever the previous one left behind.
 */
export const resetOrderConstantsCache = (): void => {
  cache = null;
};

/**
 * Order / package states and refund / gift-card reasons for filters and
 * dialogs. Serves sensible defaults immediately (and permanently, if the
 * constants query fails), refined from the API once available. `isFallback` is
 * true when we're stuck on defaults — callers surface that, because the
 * hardcoded lists are much shorter than the real ones (8 package states against
 * 18) and silently offering a subset makes a filtered list read as complete.
 */
export const useOrderConstants = (): OrderConstants & { isFallback: boolean } => {
  const [constants, setConstants] = useState<OrderConstants>(cache ?? FALLBACK);
  const [isFallback, setIsFallback] = useState(false);

  useEffect(() => {
    if (cache) return;
    let cancelled = false;
    fetchOrderConstants()
      .then((result) => {
        if (!usableConstants(result)) {
          console.error('Order constants came back unusable; keeping defaults:', result);
          if (!cancelled) setIsFallback(true);
          return;
        }
        cache = result;
        if (!cancelled) setConstants(result);
      })
      .catch((error) => {
        console.error('Falling back to default order filter options:', error);
        if (!cancelled) setIsFallback(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...constants, isFallback };
};
